import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export interface PurchaseOrder {
  id: string
  status: 'Pendente' | 'Enviado' | 'Recebido' | 'Cancelado'
  /** Data de emissão (criação do PO) — fonte do "pedido este mês" no OrcamentoMensalCard. */
  created_at: string | null
  sent_at: string | null
  received_at: string | null
  estimated_arrival: string | null
  total_value: number | null
  supplier_id: string | null
  supplier_name: string | null
  lead_time_days: number | null
}

export interface PoItem { purchase_order_id: string; product_id: string }

export function useCompras() {
  const [orders, setOrders] = useState<PurchaseOrder[]>([])
  const [poItems, setPoItems] = useState<PoItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    const [posRes, itemsRes] = await Promise.all([
      supabase
        .from('purchase_orders')
        .select('id, status, created_at, sent_at, received_at, estimated_arrival, total_value, supplier_id, suppliers(name, lead_time_days)')
        .order('created_at', { ascending: false }),
      supabase
        .from('purchase_order_items')
        .select('purchase_order_id, product_id'),
    ])

    if (posRes.error) setError(posRes.error.message)
    if (itemsRes.error) setError(prev => prev ?? itemsRes.error!.message)

    type RawPo = {
      id: string; status: string; created_at: string | null;
      sent_at: string | null; received_at: string | null;
      estimated_arrival: string | null; total_value: number | null; supplier_id: string | null;
      suppliers: { name: string; lead_time_days: number } | { name: string; lead_time_days: number }[] | null
    }

    const pos: PurchaseOrder[] = ((posRes.data ?? []) as unknown as RawPo[]).map(r => {
      const sup = Array.isArray(r.suppliers) ? r.suppliers[0] : r.suppliers
      return {
        id: r.id,
        status: r.status as PurchaseOrder['status'],
        created_at: r.created_at,
        sent_at: r.sent_at,
        received_at: r.received_at,
        estimated_arrival: r.estimated_arrival,
        total_value: r.total_value,
        supplier_id: r.supplier_id,
        supplier_name: sup?.name ?? null,
        lead_time_days: sup?.lead_time_days ?? null,
      }
    })

    setOrders(pos)
    setPoItems((itemsRes.data ?? []) as PoItem[])
    setLoading(false)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  return { orders, poItems, loading, error, refetch: fetchData }
}
